import { ArrowDown, ArrowUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { OverviewHeadline } from '@/services/org-admin/overview';

/**
 * The summary rail at the top of the Overview screen.
 *
 * Distinct from `OrgUsageStatRail`, which the Usage and Analysis tabs share, and deliberately so:
 * that rail is a borderless strip of hairline-separated figures sized to sit UNDER a page header,
 * whereas these are the first thing on the screen and carry a period-over-period delta each. Trying
 * to serve both from one component meant a `variant` prop that changed the border model, the type
 * scale and the content — three components wearing one name.
 *
 * The delta's arrow direction comes from the sign; its colour comes from `risePolarity`. A tile that
 * paints every increase green congratulates the reader on their bill going up.
 */

export interface OverviewStatTilesProps {
  headlines: OverviewHeadline[];
  isLoading?: boolean;
  ariaLabel: string;
  /**
   * Measured panel width from `useContainerWidth`, INCLUDING the content wrapper's gutter — it is
   * taken on the scroll container, which is unpadded so that every tab measures the same box.
   * `null` until the first measurement, which reads as the widest layout.
   */
  width?: number | null;
}

/**
 * Column count for four tiles.
 *
 * `auto-fit` cannot be told how many to wrap to, and with four tiles that showed: at a ~708px panel
 * exactly three tracks fitted, so the fourth dropped onto its own row beside a row-and-a-half of empty
 * space. Stating the counts keeps the grid balanced — 4, then 2×2, then stacked — and never orphans one.
 *
 * Thresholds are measured values, not guesses: the tiles need ~176px each plus an 8px gap, and the
 * number compared here carries the 32px gutter of the wrapper they sit inside.
 */
function tileColumns(width: number | null | undefined): string {
  if (width === null || width === undefined || width >= 780) return 'grid-cols-4';
  if (width >= 400) return 'grid-cols-2';
  return 'grid-cols-1';
}

function formatChange(ratio: number): string {
  const percent = Math.abs(ratio) * 100;
  // Sub-1% changes round to "0%", which reads as "no change" next to an arrow that says otherwise.
  return `${percent < 1 ? percent.toFixed(1) : Math.round(percent)}%`;
}

function Delta({ ratio, polarity }: { ratio: number; polarity: OverviewHeadline['risePolarity'] }) {
  const rising = ratio > 0;
  const Icon = rising ? ArrowUp : ArrowDown;
  /*
   * `neutral` never colours. A measure with no good direction — how many services are in use — has a
   * delta worth showing and no verdict to attach to it, and colouring it anyway would invent one.
   */
  const good = polarity === 'neutral' ? null : rising === (polarity === 'good');

  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 text-[11px] font-medium tabular-nums',
        good === null && 'text-muted-foreground',
        good === true && 'text-success-strong',
        good === false && 'text-warning-strong',
      )}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden />
      {formatChange(ratio)}
      {/* The arrow is decorative; the direction has to reach a screen reader as words. */}
      <span className="sr-only">{rising ? 'up' : 'down'} versus the previous period</span>
    </span>
  );
}

function Tile({ headline }: { headline: OverviewHeadline }) {
  return (
    <div className="oa-panel min-w-0 px-4 py-3.5">
      <div className="flex items-baseline gap-2">
        <span className="text-[22px] font-semibold leading-none tabular-nums text-foreground">
          {headline.value}
        </span>
        {headline.changeRatio !== null && (
          <Delta ratio={headline.changeRatio} polarity={headline.risePolarity} />
        )}
      </div>
      <p className="mt-2 truncate text-[13px] font-medium text-foreground">{headline.label}</p>
      {headline.hint && <p className="oa-meta mt-0.5 truncate">{headline.hint}</p>}
    </div>
  );
}

function TileSkeleton() {
  return (
    <div className="oa-panel px-4 py-3.5">
      <div className="oa-skeleton h-5 w-16" />
      <div className="oa-skeleton mt-2.5 h-3 w-24" />
      <div className="oa-skeleton mt-1.5 h-2.5 w-16" />
    </div>
  );
}

export function OverviewStatTiles({
  headlines,
  isLoading,
  ariaLabel,
  width,
}: OverviewStatTilesProps) {
  return (
    <section aria-label={ariaLabel}>
      {/*
        Column count comes from the measured PANEL width, not a viewport breakpoint: the panel sits
        inside a resizable studio shell, so the window's width is not the panel's width. Same reasoning
        as `OrgUsageStatRail` — see the note there — but an explicit count rather than `auto-fit`, for
        the orphaned-tile reason given on `tileColumns`.
      */}
      <div className={cn('grid gap-2', tileColumns(width))}>
        {isLoading
          ? Array.from({ length: 4 }).map((_, index) => <TileSkeleton key={index} />)
          : headlines.map((headline) => <Tile key={headline.id} headline={headline} />)}
      </div>
    </section>
  );
}
